/** D&D による TODO 並び替えの Optimistic Update 付きミューテーション */

import * as Sentry from "@sentry/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "../lib/api";
import { queryPersister } from "../lib/query-client";
import type { Todo } from "../types/todo";

const TODOS_KEY = ["todos"] as const;

type ReorderInput = {
  id: string;
  sortOrder: string;
};

export const useReorderTodo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, sortOrder }: ReorderInput) => {
      const res = await api[":id"].$patch({
        param: { id },
        json: { sortOrder },
      });
      if (!res.ok) throw new Error("Failed to reorder todo");
      const json = await res.json();
      return json.data;
    },
    onMutate: ({ id, sortOrder }) => {
      queryClient.cancelQueries({ queryKey: TODOS_KEY });
      const queries = queryClient.getQueriesData<Todo[]>({
        queryKey: TODOS_KEY,
      });
      const snapshots = new Map<readonly unknown[], Todo[] | undefined>();
      for (const [key, data] of queries) {
        snapshots.set(key, data);
        if (data !== undefined && !Array.isArray(data)) {
          // キャッシュの型が壊れている → クエリキャッシュのみクリア（セッションは保持）
          Sentry.captureMessage("Corrupted query cache detected, clearing", {
            level: "warning",
            extra: { key, dataType: typeof data },
          });
          queryPersister.removeClient();
          queryClient.removeQueries({ queryKey: TODOS_KEY });
          return { snapshots };
        }
        if (!Array.isArray(data)) continue;
        queryClient.setQueryData<Todo[]>(
          key,
          data
            .map((todo) => (todo.id === id ? { ...todo, sortOrder } : todo))
            .sort((a, b) => a.sortOrder.localeCompare(b.sortOrder)),
        );
      }
      return { snapshots };
    },
    onError: (err, _vars, context) => {
      if (context?.snapshots) {
        for (const [key, data] of context.snapshots) {
          queryClient.setQueryData(key, data);
        }
      }
      toast.error("並び替えに失敗しました");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TODOS_KEY });
    },
  });
};
